import React, { memo, useCallback, useMemo } from 'react';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { Delete, PushPin, PushPinOutlined, DragIndicator } from '@mui/icons-material';
import { CSS } from '@dnd-kit/utilities';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type AutoScrollOptions,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { TAnalyticParams } from '../../shared/types/analyticParameters.type';

const LIST_HEIGHT = 420;
const ITEM_HEIGHT = 50;
const ITEM_GAP = 8;

const autoScroll: AutoScrollOptions = {
    acceleration: 25, // default 10, higher = faster
    interval: 5,
    threshold: { x: 0, y: 0.2 },
};

type DraggableParamsProps = {
    selectedParams: TAnalyticParams[];
    pinnedParams: TAnalyticParams[];
    onUpdateParam: (data: TAnalyticParams[]) => void;
    updatePin: (items: TAnalyticParams[]) => void;
};

/* -------------------------------- Sortable -------------------------------- */

type SortableItemProps = {
    item: TAnalyticParams;
    index: number;
    isPinned: boolean;
    onTogglePin: (item: TAnalyticParams) => void;
    onRemove: (index: number) => void;
};

const SortableItem = memo(({ item, index, isPinned, onTogglePin, onRemove }: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
    });

    return (
        <Paper
            ref={setNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                transition,
                position: 'relative',
                zIndex: isDragging ? 10 : 0,
            }}
            elevation={isDragging ? 6 : 0}
            sx={{
                userSelect: 'none',
                height: ITEM_HEIGHT,
                flexShrink: 0,
                borderRadius: 2,
                border: '1px solid',
                borderColor: isDragging ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                boxSizing: 'border-box',
                '&:hover': { borderColor: isDragging ? 'primary.main' : 'text.disabled' },
            }}
        >
            <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        display: 'flex',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        color: 'action.active',
                        touchAction: 'none',
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    <DragIndicator fontSize="small" />
                </Box>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ color: isPinned ? 'text.primary' : 'text.secondary' }}
                >
                    {item.name_translated}
                </Typography>
            </Box>

            <Box display="flex" gap={0.5} alignItems="center">
                <IconButton
                    size="small"
                    onClick={() => onTogglePin(item)}
                    color={isPinned ? 'primary' : 'default'}
                >
                    {isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemove(index)}
                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                >
                    <Delete fontSize="small" />
                </IconButton>
            </Box>
        </Paper>
    );
});

/* --------------------------------- Main ----------------------------------- */

function DraggableParams({ selectedParams, onUpdateParam, pinnedParams, updatePin }: DraggableParamsProps) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const pinnedIds = useMemo(() => new Set(pinnedParams.map((p) => p['@id'])), [pinnedParams]);

    // Pinned items live at the top: [0, pinnedCount) are pinned, the rest are not.
    const pinnedCount = useMemo(
        () => selectedParams.reduce((n, p) => n + (pinnedIds.has(p['@id']) ? 1 : 0), 0),
        [selectedParams, pinnedIds]
    );

    const itemIds = useMemo(() => selectedParams.map((p) => p.id), [selectedParams]);

    const removeParam = useCallback(
        (index: number) => onUpdateParam(selectedParams.filter((_, i) => i !== index)),
        [selectedParams, onUpdateParam]
    );

    const togglePin = useCallback(
        (param: TAnalyticParams) => {
            if (pinnedIds.has(param['@id'])) {
                updatePin(pinnedParams.filter((p) => p['@id'] !== param['@id']));
            } else {
                updatePin([...pinnedParams, param]);
            }
        },
        [pinnedIds, pinnedParams, updatePin]
    );

    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) return;

            const oldIndex = selectedParams.findIndex((p) => p.id === active.id);
            const newIndex = selectedParams.findIndex((p) => p.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return;

            const activePinned = pinnedIds.has(selectedParams[oldIndex]['@id']);
            if (activePinned ? newIndex >= pinnedCount : newIndex < pinnedCount) return;

            onUpdateParam(arrayMove(selectedParams, oldIndex, newIndex));
        },
        [selectedParams, pinnedIds, pinnedCount, onUpdateParam]
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            autoScroll={autoScroll}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <Box
                    sx={{
                        height: LIST_HEIGHT,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${ITEM_GAP}px`,
                        pr: 0.5,
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            borderRadius: '10px',
                        },
                    }}
                >
                    {selectedParams.map((item, index) => (
                        <SortableItem
                            key={item.id}
                            item={item}
                            index={index}
                            isPinned={pinnedIds.has(item['@id'])}
                            onTogglePin={togglePin}
                            onRemove={removeParam}
                        />
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
}

export default memo(DraggableParams);